import { RouteGenerator, Client, HttpClient, Cookie, EncoderDecoderInterface } from "silver";
import { MyonLanguageLoader } from "Lang";
import { AuthTimer } from "shared/UserAuthorization/AuthTimer";
import { StorageKeyObserver } from "shared/StorageKeyObserver";
import { MyonEnv, isEnvUserAuthorized } from "shared/Api/MyonEnv";
import { UserUnauthorizedEventHandler } from "shared/UserAuthorization/UserUnauthorizedEventHandler";
import { UserActivityEventListener } from "shared/UserAuthorization/UserActivityEventListener";
import { Dialog } from "shared/Dialog";

type ExternalTimeoutConfig = {
	timeoutminutes: number;
	keepaliveintervalminutes: number;
	timetorespondminutes: number;
	keepaliveuri: string;
};

const json: EncoderDecoderInterface<number|boolean> = {
	encode: JSON.stringify,
	decode: JSON.parse
}

export class AuthListener {

	private userActivityObserver = new StorageKeyObserver<number>(localStorage, "lastClientActivity", json as EncoderDecoderInterface<number>);
	private userIdObserver = new StorageKeyObserver<number>(localStorage, "lastUserId", json as EncoderDecoderInterface<number>);
	private dialogIsOpenObserver = new StorageKeyObserver<boolean>(localStorage, "dialogIsOpen", json as EncoderDecoderInterface<boolean>);

	public readonly myonAuthTimer: AuthTimer;

	constructor(
		private env: MyonEnv,
		private httpClient: HttpClient,
		private router: RouteGenerator,
		private client: Client,
		private dialog: Dialog,
		private langLoader: MyonLanguageLoader,
		private cookie: Cookie
	) {
		this.userIdObserver.value = isEnvUserAuthorized(env) ? env.auth.userId : undefined;
		this.myonAuthTimer = this.makeAuthTimer(
			this.router.generate("login\\index", "logout", {
				logoutReason: 10,
				returnTo:     this.client.url.path
			}),
			this.router.generate("api\\touch", null, { }, "json"),
			this.env.timeout.keepaliveInterval * 1000,
			this.env.timeout.timeout * 1000,
			this.env.timeout.timeToRespond * 1000,
			true
		);

	}

	private getKeepaliveConfig(): undefined|ExternalTimeoutConfig {
		if (!this.env.keepAliveCookieName) {
			return undefined;
		}
		const cookieValue = this.cookie.getValue(this.env.keepAliveCookieName);
		if (!cookieValue) {
			return undefined;
		}
		// @TODO Shouldn't need to decode this.. that should probably happen in the cookie class
		const parsed = JSON.parse(decodeURIComponent(cookieValue));
		if (!parsed) {
			return undefined;
		}
		return parsed as ExternalTimeoutConfig;
	}

	private makeAuthTimer(logoutUri: string, touchUri: string, keepaliveMs: number, timeoutMs: number, timeToRespondMs: number, withCredentials: boolean): AuthTimer {
		return new AuthTimer(
			logoutUri,
			touchUri,
			keepaliveMs,
			timeoutMs,
			timeToRespondMs,
			withCredentials,
			this.httpClient,
			this.client,
			this.dialog,
			this.userActivityObserver,
			this.dialogIsOpenObserver,
			this.langLoader
		);
	}

	public listenForAuth(): void {
		if (!isEnvUserAuthorized(this.env)) {
			return;
		}

		const userActivityEventListener = new UserActivityEventListener(this.userActivityObserver);
		const userUnauthorizedEventHandler = new UserUnauthorizedEventHandler(this.client, this.httpClient, this.router, this.userIdObserver);

		userActivityEventListener.listen();
		userUnauthorizedEventHandler.listen();

		this.myonAuthTimer.listen();

		const externalKeepalive = this.getKeepaliveConfig();
		if (externalKeepalive) {
			const externalAuthTimer = this.makeAuthTimer(
				this.router.generate("login\\index", "logout", {
					logoutReason: 10,
					returnTo:     this.client.url.path
				}),
				externalKeepalive.keepaliveuri,
				externalKeepalive.keepaliveintervalminutes * 60000,
				externalKeepalive.timeoutminutes * 60000,
				externalKeepalive.timetorespondminutes * 60000,
				true
			);
			externalAuthTimer.keepalive().catch(console.error);
			externalAuthTimer.listen();
		}
	}
}
